import { PlusOutlined } from '@ant-design/icons'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  DEFAULT_PAGE_SIZE,
  getLoadMoreButton,
  getProjectMenuItem,
  renderSkeletonMenuItems,
  SiderMenuItem,
} from './SiderMenuItem'
import { getMenuItem, MenuItem } from '@/components/ui/common/MenuItem'
import { GitRepositoryFilled } from '@/components/ui/icons'
import urls from '@/config/uiUrls'
import { useIsLoading } from '@/hooks/useLoading'
import { useRepositories } from '@/hooks/useRepositories'
import { buildI18nFunction } from '@/utils/messages'

import { Workspace } from '@/types/index.d'

import styles from './SiderMenu.module.css'

export function RepositorySubMenu(
  workspace: Workspace,
  showPaywallModal: (id: string) => void
) {
  const t = buildI18nFunction(useIntl().formatMessage)

  const [page, setPage] = useState<number>(0)
  const [isLoading, startLoading, stopLoading] = useIsLoading()
  const [subMenuItems, setSubMenuItems] = useState<MenuItem[]>([])

  const { data, isLoading: areRepositoriesLoading } = useRepositories(
    workspace?.id,
    {
      pageIndex: page,
      pageSize: DEFAULT_PAGE_SIZE,
    }
  )

  const router = useRouter()

  useEffect(() => {
    startLoading()
    if (data?.content) {
      const repositories = data?.content
      const result = repositories?.length
        ? repositories.map((repository) =>
            getProjectMenuItem('repositories', repository)
          )
        : [
            SiderMenuItem({
              feature: 'git',
              href: urls.repositories.newRepositoryWizard,
              icon: <PlusOutlined />,
              key: `buttons.newRepository`,
              showPaywallModal,
              t,
              workspace,
              router,
            }),
          ]
      setSubMenuItems((prevState) => {
        if (!data?.first && prevState) {
          return [...prevState, ...result]
        }
        return result
      })
      stopLoading()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, data])

  const handleMoreButtonClick = () => {
    if (data && !data?.last) setPage(page + 1)
  }

  if (areRepositoriesLoading && subMenuItems.length === 0) {
    return getMenuItem(
      t('SiderMenu.repositories'),
      'repositories',
      <GitRepositoryFilled />,
      [...renderSkeletonMenuItems('repositories')]
    )
  }

  const renderRepositorySkeleton = areRepositoriesLoading
    ? renderSkeletonMenuItems('repositores')
    : []

  return {
    icon: <GitRepositoryFilled />,
    key: 'repositories',
    label: t('SiderMenu.repositories'),
    popupClassName: styles.customPopsubmenu,
    children: [
      ...subMenuItems,
      !data?.last &&
        !areRepositoriesLoading && {
          label: getLoadMoreButton(
            handleMoreButtonClick,
            'repositories',
            isLoading,
            t
          ),
        },
      ...renderRepositorySkeleton,
    ],
  }
}
